import _ from 'underscore';
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField, Label, Stack } from '@fluentui/react';
import { useAdCustomizers, getCharCountExcludingAdCustomizers } from '@bingads-webui-campaign/ad-customizers-next';
import { useAdStrengthSuggestion } from '@bingads-webui-campaign-react/responsive-search-ad-suggestion';
import { DynamicTextExtention } from './dynamic-text-extention';

import {
  validateTextField,
  adsFieldLengthLimitMap,
  AD_TYPE_RSA,
} from '../validators';
import { textFieldStyles } from '../styles';

// Bug 2751598: Cursor jump on edit text ads, updateItem() runs for too long
const UPDATE_ITEM_WAIT_IN_MS = 100;

export function BaseTextField(props) {
  const {
    configProps,
    value,
    updateItem,
    i18n,
    optional,
    adsFieldId,
    allowNewLines,
    showCharRemaining,
    showErrorMessage,
    errorMessage,
    isForVnext,
    assets,
    index,
    updateSuggestionMeasurement,
    showDynamicTextExtention,
    inputClassName,
    onBlur,
    llmAssetsSuggestions,
    adType,
    containerSelector,
    enableDKI,
    DKIThemeName,
  } = props;

  const [cachedValue, setCachedValue] = useState(value);
  const {
    key,
    ...restProps
  } = configProps;

  const charLimit = useMemo(() => adsFieldLengthLimitMap[key], [key]);

  const [charRemaining, setCharRemaining] =
    useState(charLimit - getCharCountExcludingAdCustomizers({
      text: cachedValue,
      adType,
      field: key,
    }));

  useEffect(() => {
    setCachedValue(value);
    setCharRemaining(charLimit - getCharCountExcludingAdCustomizers({
      text: value,
      adType,
      field: key,
    }));
  }, [adType, charLimit, key, value]);

  const inputRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setComponentRef = (component) => {
    // eslint-disable-next-line no-underscore-dangle
    inputRef.current = component ? _.result(component._textElement, 'current', null) : null;
  };

  const debouncedUpdateItem = useMemo(() =>
    _.debounce(updateItem, UPDATE_ITEM_WAIT_IN_MS), [updateItem]);

  const onTextFieldChange = useCallback((event, textValue, populatedWithFullValue) => {
    // if we don't allow new lines, we translate new line chararter to single space
    // so we have better format when user copy paste text with new lines in the middle
    const newValue = allowNewLines ? textValue : textValue.replace(/(\r\n|\n|\r)+/mg, ' ');
    setCachedValue(newValue);
    setCharRemaining(charLimit - getCharCountExcludingAdCustomizers({
      text: newValue,
      adType,
      field: key,
    }));

    debouncedUpdateItem(newValue, key, populatedWithFullValue);
  }, [allowNewLines, charLimit, adType, key, debouncedUpdateItem]);

  const dynamicErrorMessage = useMemo(
    () => validateTextField(key, i18n, optional, adType)(cachedValue),
    [key, i18n, optional, adType, cachedValue]
  );

  useEffect(() => {
    if (key === 'FinalUrls' && inputRef) {
      inputRef.current.focus();
    }
  }, [key, cachedValue]);

  const onBlurCheck = useCallback(() => {
    if (key === 'FinalUrls' && document.activeElement === inputRef.current) {
      return;
    }
    onBlur();
  }, [key, onBlur]);

  const onRenderDescription = useCallback(() => (
    showCharRemaining ? (
      <Stack horizontal horizontalAlign="end">
        <Label className="char-counter" styles={textFieldStyles({}).charCountDescription}>
          {charRemaining >= 0 ? i18n.getString(_TL_('{{charRemaining}} characters remaining'), { charRemaining }) :
            i18n.getString(_TL_('Exceeded by {{charRemaining}} characters'), { charRemaining: -charRemaining })}
        </Label>
        {showDynamicTextExtention ?
          <div style={textFieldStyles({}).dynamicLinkContainer}>|
            <DynamicTextExtention
              i18n={i18n}
              onTextFieldChange={onTextFieldChange}
              cachedValue={cachedValue}
            />
          </div> : null
        }

      </Stack>
    ) : null
  ), [cachedValue, charRemaining, i18n, onTextFieldChange,
    showCharRemaining, showDynamicTextExtention]);

  useAdCustomizers({
    ref: inputRef,
    updateAction: (val) => {
      setCachedValue(val);
      setCharRemaining(charLimit - getCharCountExcludingAdCustomizers({
        text: val,
        adType,
        field: key,
      }));
      debouncedUpdateItem(val, key);
    },
    adType,
    container: containerSelector,
    field: key,
  });

  useAdStrengthSuggestion({
    container: containerSelector,
    i18n,
    ref: inputRef,
    llmAssetsSuggestions,
    assets,
    assetType: configProps.key,
    updateItem: val => onTextFieldChange(null, val, true),
    isForVnext,
    index,
    updateSuggestionMeasurement,
    enableDKI,
    DKIThemeName,
  });

  return (
    <TextField
      autoComplete="off"
      componentRef={setComponentRef}
      {...restProps}
      onRenderDescription={onRenderDescription}
      validateOnLoad
      value={cachedValue}
      onChange={onTextFieldChange}
      onBlur={onBlurCheck}
      styles={textFieldStyles}
      adsFieldId={adsFieldId}
      gapCompensation={20}
      errorMessage={showErrorMessage ? (errorMessage || dynamicErrorMessage) : undefined}
      inputClassName={inputClassName}
    />
  );
}

BaseTextField.propTypes = {
  inputClassName: PropTypes.string,

  configProps: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,

  updateItem: PropTypes.func.isRequired,

  value: PropTypes.string,

  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,

  optional: PropTypes.bool,

  campaignWebsite: PropTypes.string,

  adsFieldId: PropTypes.string,

  allowNewLines: PropTypes.bool,

  isAutoAdsEnabled: PropTypes.bool,

  showCharRemaining: PropTypes.bool,

  showErrorMessage: PropTypes.bool,

  errorMessage: PropTypes.string,

  assets: PropTypes.arrayOf(PropTypes.shape({
    Asset: PropTypes.shape({
      Text: PropTypes.string.isRequired,
    }).isRequired,
    PinnedField: PropTypes.string.isRequired,
  })),

  isForVnext: PropTypes.bool,

  index: PropTypes.number,

  updateSuggestionMeasurement: PropTypes.func,

  showDynamicTextExtention: PropTypes.bool,

  onBlur: PropTypes.func,

  llmAssetsSuggestions: PropTypes.shape({
    Suggestions: PropTypes.arrayOf(PropTypes.shape({
      SuggestionId: PropTypes.string,
      Asset: PropTypes.string,
    })),
  }),
  adType: PropTypes.string,
  containerSelector: PropTypes.string,
  enableDKI: PropTypes.bool,
  DKIThemeName: PropTypes.string,
};

BaseTextField.defaultProps = {
  inputClassName: '',
  value: '',
  optional: true,
  campaignWebsite: undefined,
  adsFieldId: undefined,
  allowNewLines: true,
  isAutoAdsEnabled: true,
  showCharRemaining: false,
  showErrorMessage: false,
  errorMessage: undefined,
  isForVnext: false,
  assets: [],
  index: -1,
  updateSuggestionMeasurement: _.noop,
  showDynamicTextExtention: false,
  onBlur: _.noop,
  llmAssetsSuggestions: {},
  adType: AD_TYPE_RSA,
  containerSelector: '.rsa-content-container',
  enableDKI: false,
  DKIThemeName: '',
};
